/**
 * Échantillons — logique pure (Boîte à outils, 2nde → 1re).
 *
 * On tire 100 échantillons de taille n (probabilité ou proportion p) : chaque
 * échantillon donne une fréquence. Plus n est grand, moins les fréquences
 * s'écartent de p. Le programme de 2nde demande de calculer la proportion
 * des cas où |f − p| ≤ 1/√n : on CONSTATE qu'elle tourne autour de 95 % ou
 * plus (la probabilité est d'au moins 95 % environ pour ces situations, mais
 * une série de 100 peut descendre un peu en dessous). « Décider » (une
 * fréquence hors de la bande fait douter de p) va plus loin que le programme.
 *
 * Tailles choisies pour que 1/√n soit décimal : 25 → 0,2 ; 100 → 0,1 ;
 * 400 → 0,05 ; 2 500 → 0,02 ; 10 000 → 0,01.
 */

import { fluctuationInterval } from '../../ui/random';

export const SIZES = [25, 100, 400, 2500, 10000] as const;
export type Size = (typeof SIZES)[number];
export const SAMPLES = 100;

export interface Situation {
  id: string;
  /** puce : « Pièce équilibrée » */
  label: string;
  p: number;
  /** « 1/2 », « 1/6 », « 0,42 » */
  pText: string;
  /** « Probabilité de pile », « Proportion de gauchers dans la population » */
  pLabel: string;
  /** « de pile », « des 6 », « des gauchers » : la fréquence… */
  freqOf: string;
  /** un échantillon de taille n : « n lancers », « n personnes choisies au hasard » */
  unit: string;
  /** ce dont on doute : « la pièce soit équilibrée » */
  claim: string;
}

export const SITUATIONS: readonly Situation[] = [
  {
    id: 'coin',
    label: 'Pièce équilibrée',
    p: 0.5,
    pText: '1/2',
    pLabel: 'Probabilité de pile',
    freqOf: 'de pile',
    unit: 'lancers',
    claim: 'la pièce soit équilibrée',
  },
  {
    id: 'die',
    label: 'Dé : faire 6',
    p: 1 / 6,
    pText: '1/6',
    pLabel: 'Probabilité d’obtenir 6',
    freqOf: 'des 6',
    unit: 'lancers',
    claim: 'la probabilité d’obtenir 6 soit 1/6',
  },
  {
    id: 'blood',
    label: 'Groupe sanguin O',
    p: 0.42,
    pText: '0,42',
    pLabel: 'Proportion du groupe O dans la population',
    freqOf: 'des personnes du groupe O',
    unit: 'personnes choisies au hasard',
    claim: 'la proportion dans cette région soit 0,42',
  },
  {
    id: 'left',
    label: 'Gauchers',
    p: 0.13,
    pText: '0,13',
    pLabel: 'Proportion de gauchers dans la population',
    freqOf: 'des gauchers',
    unit: 'personnes choisies au hasard',
    claim: 'la proportion dans cette ville soit 0,13',
  },
];

export const situationById = (id: string) => SITUATIONS.find((s) => s.id === id) ?? SITUATIONS[0]!;

/** nombre de succès dans un échantillon de taille n */
export function sampleCount(n: number, p: number, rng: () => number): number {
  let k = 0;
  for (let i = 0; i < n; i++) if (rng() < p) k++;
  return k;
}

/** les fréquences de `count` échantillons de taille n */
export function drawFrequencies(
  n: number,
  p: number,
  rng: () => number,
  count = SAMPLES,
): number[] {
  return Array.from({ length: count }, () => sampleCount(n, p, rng) / n);
}

export const band = (p: number, n: number) => fluctuationInterval(p, n);

/** combien de fréquences tombent dans [p − 1/√n ; p + 1/√n] (bornes comprises) */
export function insideCount(freqs: readonly number[], p: number, n: number): number {
  const [lo, hi] = band(p, n);
  return freqs.filter((f) => f >= lo - 1e-12 && f <= hi + 1e-12).length;
}

/** étendue des fréquences : du plus petit au plus grand */
export function spread(freqs: readonly number[]): [number, number] {
  return [Math.min(...freqs), Math.max(...freqs)];
}

/** « 0,45 », « 0,1667 » : au plus 4 décimales, virgule */
export function dec(v: number, digits = 4): string {
  const r = Number(v.toFixed(digits));
  return String(Object.is(r, -0) ? 0 : r).replace('.', ',');
}

/** décimal à au plus 3 chiffres après la virgule ? (1/2 oui, 1/6 non) */
export const isDecimal = (v: number) => Math.abs(v * 1000 - Math.round(v * 1000)) < 1e-9;

/** « 2 500 » avec espace fine insécable */
export function int(n: number): string {
  return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
}

/** « [0,45 ; 0,55] » */
export function bandText(p: number, n: number): string {
  const [lo, hi] = band(p, n);
  return `[${dec(lo)}\u00a0;\u00a0${dec(hi)}]`;
}

/* ---------- décider : la fréquence observée est-elle compatible avec p ? ---------- */

export interface Decision {
  situation: Situation;
  n: number;
  /** nombre de succès observés */
  k: number;
}

export const observed = (d: Decision) => d.k / d.n;

/** la fréquence est dans l'intervalle : rien ne permet de douter de p */
export const compatible = (d: Decision) => {
  const [lo, hi] = band(d.situation.p, d.n);
  const f = observed(d);
  return f >= lo - 1e-12 && f <= hi + 1e-12;
};

/** l'énoncé : « On lance la pièce 400 fois et on obtient 230 fois pile. » */
export function decisionStatement(d: Decision): string {
  const s = d.situation;
  const n = int(d.n);
  const k = int(d.k);
  switch (s.id) {
    case 'coin':
      return `On lance une pièce ${n} fois : elle tombe ${k} fois sur pile. Peut-on douter que la pièce soit équilibrée (probabilité de pile 1/2) ?`;
    case 'die':
      return `On lance un dé ${n} fois : il donne ${k} fois un 6. Peut-on douter que la probabilité d’obtenir 6 soit 1/6 ?`;
    case 'blood':
      return `Dans une région, on choisit ${n} habitants au hasard : ${k} sont du groupe O. En France, la proportion est 0,42. Peut-on douter que la proportion dans cette région soit 0,42 ?`;
    default:
      return `Dans une ville, on choisit ${n} personnes au hasard : ${k} sont gauchères. Dans la population, la proportion de gauchers est 0,13. Peut-on douter que la proportion dans cette ville soit 0,13 ?`;
  }
}

/** l'explication de la bonne réponse */
export function decisionExplanation(d: Decision): string {
  const s = d.situation;
  const f = observed(d);
  const h = 1 / Math.sqrt(d.n);
  const fText = `f = ${int(d.k)}/${int(d.n)} = ${dec(f)}`;
  const [lo, hi] = band(s.p, d.n);
  const exact = [lo, hi].every((v) => Math.abs(v * 1e4 - Math.round(v * 1e4)) < 1e-9);
  const iText = `[${s.pText} − ${dec(h)} ; ${s.pText} + ${dec(h)}] ${exact ? '=' : '≈'} ${bandText(s.p, d.n)}`;
  return compatible(d)
    ? `${fText} est dans ${iText} : cette fréquence est habituelle si p = ${s.pText}. Rien ne permet de douter que ${s.claim} (ce n’est pas une preuve).`
    : `${fText} n’est pas dans ${iText}. Si p valait ${s.pText}, une telle fréquence n’arriverait que dans au plus 5 % des échantillons environ : on peut douter que ${s.claim}, au risque de se tromper dans au plus 5 % des cas environ.`;
}

/**
 * Cinq situations à juger : des couples (situation, n) tous différents, et
 * deux ou trois réponses « on peut douter ». Chaque fréquence est tirée au
 * hasard (avec le vrai p, ou un p décalé) jusqu'à donner la réponse voulue ;
 * la réponse attendue dépend seulement de l'intervalle.
 */
export function makeSeries(rng: () => number, count = 5): Decision[] {
  const pairs = SITUATIONS.flatMap((situation) =>
    ([100, 400, 2500] as const).map((n) => ({ situation, n })),
  );
  for (let i = pairs.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [pairs[i], pairs[j]] = [pairs[j]!, pairs[i]!];
  }
  const doubts = rng() < 0.5 ? 2 : 3;
  const want = Array.from({ length: count }, (_, i) => i < doubts);
  for (let i = want.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [want[i], want[j]] = [want[j]!, want[i]!];
  }
  return want.map((doubt, i) => {
    const { situation, n } = pairs[i % pairs.length]!;
    const h = 1 / Math.sqrt(n);
    for (let tries = 0; ; tries++) {
      const shift = doubt ? (rng() < 0.5 ? -1 : 1) * h * (1.4 + rng()) : 0;
      const p = Math.min(0.97, Math.max(0.03, situation.p + shift));
      const k = sampleCount(n, p, rng);
      const d = { situation, n, k };
      // au moins 2 succès (accords) et la réponse voulue
      if ((k >= 2 && compatible(d) === !doubt) || tries > 200) return d;
    }
  });
}
